<template>
  <div class="map-pad" id="quickMap"></div>
</template>

<script>
import {mapGetters, mapState} from 'vuex';
import {gmLoader} from '@/assets/js/google-maps';
import QuickMapInfo from '@/components/pages/dashboard/QuickMapInfo';
import {createApp} from 'vue';
import {dataManipulator, entityDataManager, GoogleMapsManager, WorkflowManager} from '@/assets/js/data-manipulation';
import i18n from '@/assets/js/vue/i18n';
import {EventSeverity} from "@/assets/js/events-info";
import moment from "moment/moment";

export default {
  name: 'QuickMapCard',
  data() {
    return {
      google: null,
      googleMap: null,
      googleMarkers: {},
      googleInfo: null,
      googleInfoWindow: null,
      drawedGeoFences: [],
    };
  },
  props: {
    selectedDeviceId: [Number, null],
    filteredDevices: Array,
    searchToken: String,
  },
  computed: {
    ...mapState(['devices', 'makes', 'models', 'vehicles', 'positions', 'drivers', 'deviceDrivers', 'events',
      'geofences', 'userGeoFences', 'deviceIgnitions']),
    ...mapGetters(['isAdmin', 'dashboardQuickMapInfoFields', 'showZonesInDashboard']),
    currentPosition() {
      return this.filteredPositions.filter(position => position.deviceId === this.selectedDeviceId)[0] || null;
    },
    googleMapPosition() {
      return this.currentPosition !== null ?
          entityDataManager.getGoogleGeoByPosition(this.currentPosition) :
          null;
    },
    filteredPositions() {
      if (!this.searchToken) {
        return this.positions;
      }

      return this.positions.filter(x => this.filteredDeviceIds.includes(x.deviceId));
    },
    filteredDeviceIds() {
      return this.filteredDevices.map(x => x.id);
    }
  },
  watch: {
    positions() {
      this.refreshMarkers();
    },
    currentPosition(newCurrentPosition) {
      this.refreshVehicleInfoWindow(newCurrentPosition);
    },
    searchToken() {
      setTimeout(() => {
        for (let key in this.googleMarkers) {
          const keyAsInt = parseInt(key);
          if (!this.filteredDeviceIds.includes(keyAsInt)) {
            const marker = this.googleMarkers[key];
            GoogleMapsManager.removeFromMap(marker);
            delete this.googleMarkers[key];
          }
        }

        this.refreshMarkers();
        if (!this.currentPosition) {
          GoogleMapsManager.closeInfoWindow(this.googleInfoWindow, this.googleInfo);
        }
      }, 100);
    },
    userGeoFences() {
      this.drawUserGeoFences();
    },
    showZonesInDashboard() {
      this.drawUserGeoFences();
    }
  },
  mounted() {
    gmLoader.load().then((google) => {
      this.google = google;
      this.initGoogleMap();
      this.drawUserGeoFences();

      if (this.positions.length > 0) {
        this.refreshMarkers();
      }

      const position = this.googleMapPosition ?? this.getFirstDevicePosition();
      if (position !== null) {
        this.setCenter(position);
      }

      this.google.maps.event.addListener(this.googleMap, 'idle', () => {
        const bounds = this.googleMap.getBounds();
        for (let key in this.googleMarkers) {
          const marker = this.googleMarkers[key];
          if (bounds.contains(marker.getPosition())) {
            marker.setVisible(true);
          } else {
            marker.setVisible(false);
          }
        }
      });
    });
  },
  methods: {
    initGoogleMap() {
      this.googleMap = new this.google.maps.Map(document.getElementById('quickMap'), {
        center: GoogleMapsManager.defaultCenter,
        zoom: 13,
        mapTypeControlOptions: {
          mapTypeIds: [
            this.google.maps.MapTypeId.ROADMAP,
            this.google.maps.MapTypeId.HYBRID
          ]
        },
        styles: [
          {
            featureType: "transit",
            elementType: "labels",
            stylers: [
              { visibility: "off" }
            ]
          }
        ]
      });
    },
    setCenter({lat, lng}) {
      if (!this.googleMap) {
        return;
      }

      this.googleMap.setCenter({lat, lng});
    },
    centerSelectedDevice() {
      if (!this.googleMapPosition) {
        return;
      }

      this.setCenter(this.googleMapPosition);
    },
    focusSelectedDevice() {
      this.googleMap.setZoom(16);
    },
    openSelectedDeviceInfoWindow() {
      if (!this.currentPosition) {
        return;
      }

      this.openVehicleInfoWindow(this.currentPosition);
    },
    refreshMarkers() {
      if (!this.google || !this.googleMap) {
        return;
      }

      this.filteredPositions.forEach(position => {

        const vehicle = entityDataManager.getVehicleByDeviceId(this.vehicles, position.deviceId);

        const ignition = entityDataManager.getDeviceIgnition(position.deviceId, this.deviceIgnitions);
        const geoPos = entityDataManager.getGoogleGeoByPosition(position);
        const icon = {
          path: this.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 5,
          fillColor: ignition ? 'green' : 'red',
          fillOpacity: 0.8,
          strokeWeight: 1,
          rotation: position.course || 0,
        };

        if (geoPos.lat === 0 || geoPos.lng === 0) {
          return;
        }

        if (this.googleMarkers[position.deviceId] === undefined) {
          this.googleMarkers[position.deviceId] = new this.google.maps.Marker({
            position: geoPos,
            map: this.googleMap,
            icon: icon,
            ...(
                vehicle
                    ? {
                      label: {
                        className: 'map-label',
                        text: vehicle.shortName,
                      },
                    }
                    : {}
            ),
          });

          this.google.maps.event.addListener(
              this.googleMarkers[position.deviceId],
              'click',
              () => this.onSelectMarker(position.deviceId),
          );
        }

        let marker = this.googleMarkers[position.deviceId];
        marker.setPosition(geoPos);
        marker.setIcon(icon);
      });
    },
    drawUserGeoFences() {
      for (const geoFence of this.drawedGeoFences) {
        GoogleMapsManager.removeFromMap(geoFence);
      }
      this.drawedGeoFences = [];

      if (!this.showZonesInDashboard || !this.google?.maps || !this.googleMap) {
        return;
      }

      for (const userGeoFence of this.userGeoFences) {
        const area = GoogleMapsManager.drawGeoFence(this.google.maps, userGeoFence, this.googleMap,
            (data) => this.openGeoFenceInfoWindow(data));
        this.drawedGeoFences.push(area);
      }
    },
    onSelectMarker(deviceId) {
      this.selectDeviceId(deviceId);
      const selectedPosition = this.positions.filter(position => position.deviceId === deviceId)[0] || null;
      if (selectedPosition) {
        this.openVehicleInfoWindow(selectedPosition);
      }
    },
    onShowTrips() {
      if (!this.currentPosition) {
        return;
      }

      const fromDate = moment().startOf('day').toDate();
      const toDate = moment().endOf('day').toDate();

      WorkflowManager.openTripsMaps(this.currentPosition.deviceId, fromDate, toDate, this.currentPosition.fixTime);
    },
    selectDeviceId(id) {
      this.$emit('on-select-device', id);
    },
    refreshVehicleInfoWindow(position) {
      if (!this.googleInfoWindow?.isOpen || !position) {
        return;
      }

      this.openVehicleInfoWindow(position);
    },
    openVehicleInfoWindow(position) {
      GoogleMapsManager.unmountComponent(this.googleInfo);
      const props = this.getVehicleInfoWindowContent(position);
      const geoPos = entityDataManager.getGoogleGeoByPosition(position);
      this.googleInfo = createApp(QuickMapInfo, props).use(i18n);
      this.googleInfoWindow = GoogleMapsManager.showInfoWindow(this.googleInfoWindow, this.googleInfo,
          geoPos, this.googleMap, this.google.maps);
    },
    getVehicleInfoWindowContent(position) {
      const vehicle = entityDataManager.getVehicleByDeviceId(this.vehicles, position.deviceId);
      const device = entityDataManager.getById(this.devices, position.deviceId);

      let beaconUUID = this.deviceDrivers.get(vehicle.deviceId)?.beaconUUID;

      let engineHours = null;
      if (vehicle?.engineHoursSupported) {
        const engineTime = entityDataManager.getEngineTimeByPosition(position);
        const hours = dataManipulator.millisecondsToTime(engineTime);
        engineHours = this.$t('reports.trips.result.engine-hours', hours);
      }

      return {
        showTripsButton: true,
        onShowTrips: () => this.onShowTrips(),
        deviceId: vehicle.deviceId,
        carTitle: vehicle.fullName || this.$t('dashboard.quick-map.info-window.no-car-title'),
        deviceTitle: device.name || this.$t('dashboard.quick-map.info-window.no-car-title'),
        address: entityDataManager.getAddressByPosition(position),
        ignition: entityDataManager.getDeviceIgnition(vehicle.deviceId, this.deviceIgnitions),
        speedStatus: entityDataManager.getSpeedStatusByPosition(position),
        fuel: entityDataManager.getFuelByPosition(position, vehicle.tankCapacity),
        fuelCapStatus: entityDataManager.getFuelCapStatusByPosition(position),
        engineHours: engineHours,
        gpsParams: entityDataManager.getGpsParamsByPosition(position),
        driver: entityDataManager.getDriverByBeaconUUID(beaconUUID, this.drivers, '-'),
        lastUpdateTime: entityDataManager.getTimeByPosition(position),
        visibleFields: this.dashboardQuickMapInfoFields,
        alarm: () => {
          const event = entityDataManager.getLastNonSeenEventByDeviceIdAndSeverity(this.events, vehicle.deviceId, EventSeverity.warning);
          if (!event) {
            return null;
          }

          const geoFenceName = entityDataManager.getGeoFenceName(event.geofenceId, this.geofences);
          return entityDataManager.getTranslatedMessageByEvent(event.type, event.attributes?.alarm, geoFenceName, event.maintenanceName);
        }
      };
    },
    openGeoFenceInfoWindow(geoFence) {
      GoogleMapsManager.unmountComponent(this.googleInfo);
      this.googleInfo = GoogleMapsManager.createGeoFenceInfoWindow(geoFence);
      this.googleInfoWindow = GoogleMapsManager.showInfoWindow(this.googleInfoWindow, this.googleInfo,
          geoFence.area.coordinates, this.googleMap, this.google.maps);
    },
    getFirstDevicePosition() {
      if (this.devices.length === 0) {
        return null;
      }

      const position = this.positions.filter(position => position.deviceId === this.devices[0].id)[0] || null;
      if (!position) {
        return null;
      }

      return entityDataManager.getGoogleGeoByPosition(position);
    }
  },
};
</script>

<style scoped lang="scss">
.map-pad {
  width: calc(100% - 40px);
  height: 760px;
  margin: 20px;

  border-radius: 30px;
}
</style>
